// =============================================================================
// api/emailTemplates.ts — typed client for the booking-email-templates function
// =============================================================================
// The transactional mails (ticket confirmation, time change, day-of reminder,
// manage link, betaallink, gift voucher, credit voucher). Each can be replaced
// by a template written here, in the same content model as a campaign
// (per-locale variants, blocks or pasted HTML). On top of the campaign tokens
// every mail has its own: values such as {{boekingsnummer}} and SECTIONS such
// as {{qr_code}} that expand into a ready-made part of the mail.
//
// No template, or one that is switched off, means the built-in mail goes out
// exactly as before. The built-in mail is also what goes out when a template
// fails to render, so a template can never stop a ticket from arriving.
import { BOOKING_API_URL, supabase } from '../lib/supabase';
import type { CampaignContent, CampaignLocale, RenderedPreview } from './marketing';

export type TransactionalKind =
  | 'confirmation'
  | 'rescheduled'
  | 'arrival_reminder'
  | 'manage_link'
  | 'payment_request'
  | 'gift_voucher'
  | 'refund_voucher';

export interface TokenDoc {
  token: string;
  label: string;
  // Expands into a ready-made block (QR card, table) rather than a value.
  section?: boolean;
  // A yes/no condition for {{#token}} … {{/token}}, not a value.
  condition?: boolean;
}

export interface SavedTemplate {
  kind: TransactionalKind;
  content: CampaignContent;
  default_locale: CampaignLocale;
  enabled: boolean;
  updated_at: string;
  updated_by_operator_id: string | null;
}

export interface TransactionalMail {
  kind: TransactionalKind;
  label: string;
  when: string;
  tokens: TokenDoc[];
  // Tokens a sensible mail of this kind contains; the editor warns when one is
  // missing, it does not refuse.
  expected: string[];
  // A starting point that reads like today's built-in mail.
  defaults: CampaignContent;
  template: SavedTemplate | null;
}

export interface EmailTemplatesError {
  error: string;
  message?: string;
}

export interface EmailTemplatesResult<T> {
  ok: boolean;
  data: T | null;
  error: EmailTemplatesError | null;
  status: number;
}

// Which sample booking the preview and test mail use: unpaid shows the
// payment banner, split spreads the group over two time slots, no_vat puts
// the venue on 0% VAT, reduced makes the credit voucher one for seats taken
// off a booking.
export interface SampleScenario {
  unpaid?: boolean;
  split?: boolean;
  no_vat?: boolean;
  reduced?: boolean;
  location_id?: string | null;
}

async function call<T>(
  action: string,
  body: Record<string, unknown> = {},
): Promise<EmailTemplatesResult<T>> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/booking-email-templates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ action, ...body }),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: { error: 'network_error', message: e instanceof Error ? e.message : String(e) },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    return {
      ok: false,
      data: null,
      error: (json as EmailTemplatesError) ?? { error: 'unknown_error', message: `HTTP ${res.status}` },
      status: res.status,
    };
  }
  return { ok: true, data: json as T, status: res.status, error: null };
}

export function listEmailTemplates() {
  return call<{ mails: TransactionalMail[]; from_address: string }>('list');
}

export function saveEmailTemplate(
  kind: TransactionalKind,
  input: { content: CampaignContent; default_locale: CampaignLocale; enabled?: boolean },
) {
  return call<{ ok: true; template: SavedTemplate }>('save', { kind, ...input });
}

// Deletes the template: the built-in mail goes out again.
export function resetEmailTemplate(kind: TransactionalKind) {
  return call<{ ok: true }>('reset', { kind });
}

// content null = the built-in mail as it goes out today.
export function previewEmailTemplate(
  kind: TransactionalKind,
  content: CampaignContent | null,
  locale: CampaignLocale,
  defaultLocale: CampaignLocale,
  scenario: SampleScenario,
) {
  return call<RenderedPreview & { source: 'template' | 'builtin' }>('preview', {
    kind,
    ...(content ? { content } : {}),
    locale,
    default_locale: defaultLocale,
    ...scenario,
  });
}

// Sends what is in the editor, saved or not. `to` empty = yourself.
export function testSendEmailTemplate(
  kind: TransactionalKind,
  content: CampaignContent | null,
  locale: CampaignLocale,
  defaultLocale: CampaignLocale,
  scenario: SampleScenario,
  to?: string,
) {
  return call<{ to: string; locale: string; sample_names: boolean; builtin: boolean; resend_id: string }>(
    'test_send',
    {
      kind,
      ...(content ? { content } : {}),
      locale,
      default_locale: defaultLocale,
      ...scenario,
      ...(to ? { to } : {}),
    },
  );
}
